import { Alert, Button } from '@material-ui/core';
import CrmMemberAvatar from 'components/_dashboard/crm/org/CrmMemberAvatar';
import type { CrmHandlerView } from 'components/_dashboard/crm/handler/crmHandlerFace';

/**
 * WHAT THE NEW-CONTACT FORM SAYS ABOUT A PHONE NUMBER THAT IS ALREADY SPOKEN FOR.
 *
 * Two independent facts, from two lookups that land at different times:
 *
 *   1. I ALREADY HAVE THIS PERSON. Answered by `EditContactDialog`'s own dedupe — the
 *      in-browser contact array, falling back to one Firestore read of the caller's OWN
 *      book. It has always been here.
 *   2. A COLLEAGUE IS ALREADY HANDLING THIS PERSON. Answered by the מתווך מטפל lookup,
 *      which neither of the above can see: both are scoped to `dashboard-users-v2/{me}`.
 *
 * ── ONE ALERT, NEVER TWO STACKED ───────────────────────────────────────────────
 *
 * Both facts are about the same number and arrive milliseconds apart. Two alerts would be
 * two layout steps, under a field the user is still typing into, for one event.
 *
 * ── THE SEVERITY IS HONEST RATHER THAN COSMETIC ────────────────────────────────
 *
 * A duplicate in my own book is a convenience — "you already have this, edit it instead" —
 * which is `info`, and is what this alert has always been. A colleague's name changes an
 * INTERPERSONAL decision, so it is `warning`, it takes the colleague's FACE as the alert
 * icon, and its sentence comes FIRST. `ערוך במקום` stays the action either way, so the
 * existing muscle memory is untouched.
 *
 * ── IT NEVER BLOCKS THE SAVE ───────────────────────────────────────────────────
 *
 * `isDuplicatePhone` keeps whatever veto it already had in the form; this component adds
 * none. The product decision is a SOFT guardrail, and making creation itself confirmable
 * would be the one place that crossed into blocking.
 *
 * ── WHY IT IS ITS OWN FILE ─────────────────────────────────────────────────────
 *
 * `EditContactDialog` is ~2,600 lines with ~30 hooks, and `eslint-plugin-react-hooks@4.2.0`
 * starts mis-analysing its control flow — reporting every hook in it as conditionally
 * called — once enough JSX is added inline. Extracting the markup is the fix, and it is
 * better factoring regardless: the two-fact matrix below is real logic and is now testable
 * on its own.
 */
export type ContactPhoneDuplicateAlertProps = {
  /** The caller already holds this phone in their own book. */
  isDuplicatePhone: boolean;
  /** The existing contact's display name, for the duplicate half of the sentence. */
  existingName?: string;
  /**
   * A COLLEAGUE designated to handle this person.
   *
   * The caller is expected to have filtered out themselves and a departed handler already —
   * never tell a user about themselves, which is the fastest way to make a warning
   * ignorable, and a stale designation names somebody who cannot be coordinated with.
   */
  collidingHandler: CrmHandlerView | null;
  /** Hydrates the form from the existing contact. Only meaningful when duplicate. */
  onEditExisting: () => void;
  /** `contactsPage.newContact.*` — passed in so this file needs no i18n hook. */
  existsLabel: string;
  editInsteadLabel: string;
};

export default function ContactPhoneDuplicateAlert({
  isDuplicatePhone,
  existingName,
  collidingHandler,
  onEditExisting,
  existsLabel,
  editInsteadLabel
}: ContactPhoneDuplicateAlertProps): JSX.Element | null {
  // Nothing known, or nothing worth saying. A PENDING or FAILED handler lookup arrives here
  // as `null` and renders nothing — it must never become an affirmative "nobody is handling
  // this", which is the one wrong answer this surface can give.
  if (!isDuplicatePhone && !collidingHandler) return null;

  return (
    <Alert
      severity={collidingHandler ? 'warning' : 'info'}
      sx={{ mt: 1, alignItems: 'center' }}
      icon={
        collidingHandler ? (
          <CrmMemberAvatar name={collidingHandler.name} avatarUrl={collidingHandler.avatarUrl} size={22} />
        ) : undefined
      }
      action={
        isDuplicatePhone ? (
          <Button size="small" onClick={onEditExisting} sx={{ fontWeight: 800 }}>
            {editInsteadLabel}
          </Button>
        ) : undefined
      }
    >
      {collidingHandler && <b>{collidingHandler.name}</b>}
      {collidingHandler && ' מטפל/ת בלקוח הזה מטעם המשרד.'}
      {collidingHandler && !isDuplicatePhone && ' אפשר להמשיך ולשמור כרטיס משלכם — כדאי לתאם קודם.'}
      {collidingHandler && isDuplicatePhone && ' '}
      {isDuplicatePhone && existsLabel}
      {isDuplicatePhone && ' — '}
      {isDuplicatePhone && <b>{existingName ?? ''}</b>}
    </Alert>
  );
}
